import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import {
  GetUserSubscriptionsGlobal,
  IUserSubscriptionWithOrder,
} from "../../services/UserSubscriptionServices";
import { formatCurrency } from "../../utils/commons";
import UserCell from "../../components/UserCell";
import DateRangePicker from "../../components/DateRangePicker";
import { CancelSubscriptionDialog } from "../members/CancelSubscriptionDialog";
import {
  MemberSubscriptionsData,
  StartSubscription,
} from "../../services/MembersService";
import { OrderDetailDrawer } from "./OrderDetailDrawer";

const STATUS_OPTIONS = [
  { value: "ALL", label: "All Statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "PAUSED", label: "Paused" },
  { value: "GRANTED", label: "Granted" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXPIRED", label: "Expired" },
  { value: "INACTIVE", label: "Inactive" },
];

const getStatusColor = (status: string) => {
  const colors: Record<
    string,
    "success" | "error" | "warning" | "default" | "info"
  > = {
    ACTIVE: "success",
    PAUSED: "warning",
    GRANTED: "info",
    CANCELLED: "error",
    EXPIRED: "warning",
    INACTIVE: "default",
  };
  return colors[status] || "default";
};

const getPaymentColor = (status: string) => {
  if (status === "PAID" || status === "PAID_MANUAL") return "success";
  if (status === "REFUNDED") return "warning";
  if (status === "FAILED") return "error";
  return "info";
};

const formatDate = (date: string) =>
  date
    ? new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      })
    : "N/A";

const UserSubscriptionsList = (): JSX.Element => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<
    IUserSubscriptionWithOrder[]
  >([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Filters
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Dialogs
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedSub, setSelectedSub] =
    useState<MemberSubscriptionsData | null>(null);
  const [startingSubId, setStartingSubId] = useState<string | null>(null);
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await GetUserSubscriptionsGlobal({
        page: currentPage,
        size: rowsPerPage,
        status: status !== "ALL" ? status : undefined,
        search: search || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      setSubscriptions(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
    } catch (error) {
      enqueueSnackbar("Failed to load subscriptions", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    status,
    search,
    startDate,
    endDate,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleSearch = () => {
    setSearch(searchInput);
    setCurrentPage(0);
  };

  const handleCancelClick = (sub: IUserSubscriptionWithOrder) => {
    setSelectedSub({
      id: sub.id,
      createdAt: sub.createdAt,
      endDate: sub.endDate,
      startDate: sub.startDate,
      status: sub.status,
      subscription: sub.subscription as any,
      user: sub.user as any,
    });
    setCancelDialogOpen(true);
  };

  const handleStartClick = async (sub: IUserSubscriptionWithOrder) => {
    try {
      setStartingSubId(sub.id);
      await StartSubscription({ userSubscriptionId: sub.id });
      enqueueSnackbar("Subscription started successfully", {
        variant: "success",
      });
      await fetchSubscriptions();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to start subscription";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setStartingSubId(null);
    }
  };

  const openMember = (userId: string) => {
    navigate("/members", { state: { selectedMemberId: userId } });
  };

  const renderAmountCell = (sub: IUserSubscriptionWithOrder) => {
    if (!sub.order) {
      return "-";
    }

    const amount = sub.order.amount;
    const refundedAmount = sub.order.refundedAmount;
    const hasRefund = refundedAmount !== null && refundedAmount !== undefined;

    if (!hasRefund) {
      return formatCurrency(amount);
    }

    const safeRefundedAmount = Math.max(0, refundedAmount);
    const isFullRefund = Math.abs(safeRefundedAmount - amount) < 0.01;
    const retainedAmount = Math.max(amount - safeRefundedAmount, 0);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          lineHeight: 1.3,
        }}
      >
        <Typography
          variant="body2"
          sx={{ textDecoration: "line-through", color: "text.secondary" }}
        >
          {formatCurrency(amount)}
        </Typography>

        {isFullRefund ? (
          <Typography
            variant="caption"
            sx={{ color: "success.main", fontWeight: 500 }}
          >
            ↩ Full refund
          </Typography>
        ) : (
          <>
            <Typography
              variant="caption"
              sx={{ color: "warning.main", fontWeight: 500 }}
            >
              ↩ {formatCurrency(safeRefundedAmount)} refunded
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {formatCurrency(retainedAmount)} retained
            </Typography>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Filters */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <TextField
          placeholder="Search by name or phone"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" size="small" onClick={handleSearch}>
          Search
        </Button>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => {
              setStatus(e.target.value);
              setCurrentPage(0);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={({ startDate: nextStartDate, endDate: nextEndDate }) => {
            setStartDate(nextStartDate);
            setEndDate(nextEndDate);
            setCurrentPage(0);
          }}
        />

        {(search || status !== "ALL" || startDate || endDate) && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setStatus("ALL");
              setStartDate("");
              setEndDate("");
              setCurrentPage(0);
            }}
            startIcon={<ClearIcon />}
          >
            Clear
          </Button>
        )}
      </Box>

      {/* Table */}
      <TableContainer sx={{ flex: 1, minHeight: 0, scrollbarWidth: "none" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.info.light }}>
              <TableCell>User</TableCell>
              <TableCell>Subscription</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Duration</TableCell>
              <TableCell align="center">Payment</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <SkeletonRows />
            ) : subscriptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary" sx={{ py: 4 }}>
                    No subscriptions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              subscriptions.map((sub) => (
                <TableRow key={sub.id} hover>
                  <TableCell>
                    <UserCell user={sub.user} onOpenMember={openMember} />
                  </TableCell>
                  <TableCell>{sub.subscription?.name || "N/A"}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={sub.status}
                      color={getStatusColor(sub.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {formatDate(sub.startDate)} - {formatDate(sub.endDate)}
                  </TableCell>
                  <TableCell align="center">
                    {sub.order ? (
                      <Chip
                        label={
                          sub.order.paymentStatus === "PAID_MANUAL"
                            ? "Manual"
                            : sub.order.paymentStatus
                        }
                        color={getPaymentColor(sub.order.paymentStatus)}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="right">{renderAmountCell(sub)}</TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      {sub.order && (
                        <Tooltip title="View Order">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedOrderId(sub.order!.orderId);
                              setOrderDrawerOpen(true);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {sub.status === "GRANTED" && (
                        <Tooltip title="Start Subscription">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleStartClick(sub)}
                            disabled={startingSubId === sub.id}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {sub.status === "ACTIVE" && (
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelClick(sub)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          px: 1,
          py: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Typography variant="body2">
          Total: <b>{totalElements}</b> | Page <b>{currentPage + 1}</b> of{" "}
          {totalPages || 1}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 95 }}>
            <InputLabel>Rows</InputLabel>
            <Select
              value={rowsPerPage}
              label="Rows"
              onChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setCurrentPage(0);
              }}
            >
              {[10, 15, 25, 50].map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={loading || currentPage === 0}
          >
            Previous
          </Button>
          <Button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={
              loading || currentPage >= totalPages - 1 || totalPages === 0
            }
          >
            Next
          </Button>
        </Box>
      </Box>

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onSuccess={fetchSubscriptions}
        subscription={selectedSub}
      />

      <OrderDetailDrawer
        open={orderDrawerOpen}
        onClose={() => setOrderDrawerOpen(false)}
        orderId={selectedOrderId}
        onRefundSuccess={fetchSubscriptions}
      />
    </Box>
  );
};

const SkeletonRows = () => (
  <>
    {[...Array(5)].map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <Skeleton height={35} />
        </TableCell>
        <TableCell>
          <Skeleton height={35} />
        </TableCell>
        <TableCell>
          <Skeleton height={25} />
        </TableCell>
        <TableCell>
          <Skeleton height={25} />
        </TableCell>
        <TableCell>
          <Skeleton height={25} />
        </TableCell>
        <TableCell>
          <Skeleton height={25} />
        </TableCell>
        <TableCell>
          <Skeleton height={35} />
        </TableCell>
      </TableRow>
    ))}
  </>
);

export default UserSubscriptionsList;
